import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Form, Button, Modal, Row, Col, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import { Event, Client, Location, EventColor } from '../interfaces';
import MessageModal from './MessageModal';
import ClientForm from './ClientForm';
import LocationForm from './LocationForm';
// אייקונים
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faTimes, 
  faCalendarAlt, 
  faUser, 
  faLocationArrow, 
  faInfoCircle, 
  faCheck, 
  faExclamationTriangle 
} from '@fortawesome/free-solid-svg-icons';

import { API_BASE_URL, buildApiUrl } from '../config/api';

// ממשק לנתוני הטופס
interface EventFormData {
  id?: number;
  event_name: string;
  client: number | '';
  free_text_client: string | null; // חדש: טקסט חופשי עבור לקוח
  start_date: string;
  end_date: string;
  location: number | '' | null;
  free_text_location: string | null; // חדש: טקסט חופשי עבור מיקום
  is_internal: boolean; // חדש: סוג אירוע
  description_amplification: string | null;
  description_lighting: string | null;
  description_video: string | null;
  description_electrical: string | null;
  description_general: string | null;
  status: string;
  color: number | null;
  parent_event: number | null;
}

interface EventFormProps {
  eventToEdit?: Event | null;
  // נעביר חזרה את האירוע שנשמר (חדש או מעודכן)
  onSuccess: (savedEvent: Event) => void;
  onCancel: () => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
}

const EventForm: React.FC<EventFormProps> = ({ eventToEdit, onSuccess, onCancel, initialStartDate, initialEndDate }) => {
  const { user } = useAuth();
  const isTeamLeader = user?.role?.name === 'TEAM_LEADER';
  const [formData, setFormData] = useState<EventFormData>({
    event_name: '',
    client: '',
    free_text_client: null,
    start_date: '',
    end_date: '',
    location: '',
    free_text_location: null,
    is_internal: true,
    description_amplification: null,
    description_lighting: null,
    description_video: null,
    description_electrical: null,
    description_general: null,
    status: 'planned',
    color: null,
    parent_event: null,
  });

  const [startDateOnly, setStartDateOnly] = useState('');
  const [startTimeOnly, setStartTimeOnly] = useState('');
  const [endDateOnly, setEndDateOnly] = useState('');
  const [endTimeOnly, setEndTimeOnly] = useState('');
  
  const [clients, setClients] = useState<Client[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [eventColors, setEventColors] = useState<EventColor[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  
  const [loadingDeps, setLoadingDeps] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [showClientModal, setShowClientModal] = useState<boolean>(false);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [reloadFlag, setReloadFlag] = useState<number>(0);
  
  // הודעות מעוצבות
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  
  // Modal לבדיקת זמינות ציוד
  const [showEquipmentAvailabilityModal, setShowEquipmentAvailabilityModal] = useState<boolean>(false);
  const [equipmentAvailabilityData, setEquipmentAvailabilityData] = useState<any>(null);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  const STATUS_CHOICES = [
    { value: 'planned', label: 'מתוכנן' },
    { value: 'preparation', label: 'בהכנה' },
    { value: 'active', label: 'פעיל/בחוץ' },
    { value: 'completed', label: 'הסתיים' },
    { value: 'cancelled', label: 'בוטל' },
    { value: 'no_equipment_list', label: 'ללא רשימת ציוד' },
  ];

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  // פונקציות לטיפול בבדיקת זמינות ציוד
  const handleEquipmentAvailabilityConfirm = async () => {
    setShowEquipmentAvailabilityModal(false);
    setSubmitLoading(true);
    
    try {
      if (!eventToEdit?.id) {
        throw new Error('לא נמצא מזהה אירוע לעדכון.');
      }
      const response = await axios.put<Event>(
        buildApiUrl(`events/events/${eventToEdit.id}/`),
        pendingFormData
      );
      onSuccess(response.data);
    } catch (err) {
      console.error("שגיאה בעדכון אירוע:", err);
      showMessage('שגיאה', 'שגיאה בעדכון האירוע. אנא נסה שוב.', 'danger');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEquipmentAvailabilityCancel = () => {
    setShowEquipmentAvailabilityModal(false);
    setEquipmentAvailabilityData(null);
    setPendingFormData(null);
    setSubmitLoading(false);
  };

  const fetchDependencies = async () => {
    setLoadingDeps(true);
    try {
      const [clientsRes, locationsRes, colorsRes, eventsRes] = await Promise.all([
        axios.get<Client[]>(buildApiUrl('settings/clients/')),
        axios.get<Location[]>(buildApiUrl('settings/locations/')),
        axios.get<EventColor[]>(buildApiUrl('events/event-colors/')),
        axios.get<Event[]>(buildApiUrl('events/events/')),
      ]);
      setClients(clientsRes.data);
      setLocations(locationsRes.data);
      setEventColors(colorsRes.data);
      setEvents(eventsRes.data);
    } catch (err) {
      console.error("שגיאה בטעינת נתונים נדרשים לטופס:", err);
      setError("לא ניתן לטעון נתונים נדרשים (לקוחות, מיקומים).");
    } finally {
      setLoadingDeps(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, [reloadFlag]);

  useEffect(() => {
    if (eventToEdit) {
      const [amplification, lighting, video, electrical, general] = eventToEdit.description?.split('---SPEC_DELIMITER---') || ['', '', '', '', ''];
      
      // קבע אם האירוע בפנים או בחוץ לפי המיקום שנבחר
      const selectedLocation = locations.find(loc => loc.id === eventToEdit.location);
      const isIndoor = selectedLocation?.location_type?.includes('פנים') || eventToEdit.is_internal;
      
      setFormData({
        id: eventToEdit.id,
        event_name: eventToEdit.event_name,
        client: eventToEdit.client || '',
        free_text_client: eventToEdit.free_text_client || null,
        start_date: eventToEdit.start_date,
        end_date: eventToEdit.end_date,
        location: eventToEdit.location || '',
        free_text_location: eventToEdit.free_text_location || null,
        is_internal: isIndoor,
        description_amplification: amplification || null,
        description_lighting: lighting || null,
        description_video: video || null,
        description_electrical: electrical || null,
        description_general: general || null,
        status: eventToEdit.status,
        color: eventToEdit.color || null,
        parent_event: eventToEdit.parent_event || null,
      });

      if (eventToEdit.start_date) {
        setStartDateOnly(moment(eventToEdit.start_date).format('YYYY-MM-DD'));
        setStartTimeOnly(moment(eventToEdit.start_date).format('HH:mm'));
      }
      if (eventToEdit.end_date) {
        setEndDateOnly(moment(eventToEdit.end_date).format('YYYY-MM-DD'));
        setEndTimeOnly(moment(eventToEdit.end_date).format('HH:mm'));
      }
    } else if (initialStartDate && initialEndDate) {
        setFormData(prev => ({
            ...prev,
            start_date: moment(initialStartDate).toISOString(),
            end_date: moment(initialEndDate).toISOString(),
            parent_event: null,
        }));
        setStartDateOnly(moment(initialStartDate).format('YYYY-MM-DD'));
        setStartTimeOnly(moment(initialStartDate).format('HH:mm'));
        setEndDateOnly(moment(initialStartDate).format('YYYY-MM-DD'));
        setEndTimeOnly('23:59');
    }
    else {
        setFormData({
            event_name: '', client: '', free_text_client: null, start_date: '', end_date: '',
            location: '', free_text_location: null, is_internal: false,
            description_amplification: null,
            description_lighting: null,
            description_video: null,
            description_electrical: null,
            description_general: null,
            status: 'planned',
            color: null,
            parent_event: null,
        });
        setStartDateOnly('');
        setStartTimeOnly('');
        setEndDateOnly('');
        setEndTimeOnly('');
    }
  }, [eventToEdit, initialStartDate, initialEndDate]);
  
  useEffect(() => {
    if (startDateOnly && !endDateOnly) {
      setEndDateOnly(startDateOnly);
    }
    // ברירת מחדל לשעות לאחר בחירת תאריך: התחלה 00:00, סיום 23:59
    if (startDateOnly && !startTimeOnly) {
      setStartTimeOnly('00:00');
    }
    if ((endDateOnly || startDateOnly) && !endTimeOnly) {
      setEndTimeOnly('23:59');
    }
  }, [startDateOnly, endDateOnly, startTimeOnly, endTimeOnly]);

  // תיקון אוטומטי בצד לקוח: ודא שתאריך/שעת הסיום לא לפני ההתחלה
  useEffect(() => {
    if (!startDateOnly) return;
    // אם תאריך הסיום לפני תאריך ההתחלה – השווה לתאריך ההתחלה
    if (endDateOnly && endDateOnly < startDateOnly) {
      setEndDateOnly(startDateOnly);
    }
    // אם אותו יום – ודא ששעת סיום לא לפני שעת התחלה
    if ((endDateOnly || '') === startDateOnly) {
      const st = startTimeOnly || '';
      const et = endTimeOnly || '';
      if (st && et && et < st) {
        setEndTimeOnly(st);
      }
    }
  }, [startDateOnly, startTimeOnly, endDateOnly, endTimeOnly]);

  const checkStatusRestrictions = (newStatus: string) => {
    if (!startDateOnly) return true;
    
    const now = new Date();
    const eventDate = new Date(startDateOnly + ' ' + (startTimeOnly || '00:00'));
    const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // בדיקה שלא ניתן לשנות סטטוס ל"בהכנה" לפני השבוע שלפני התאריך
    if (newStatus === 'preparation' && now < oneWeekBefore) {
      const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      showMessage('שגיאה', `לא ניתן לשנות סטטוס ל"בהכנה" לפני השבוע שלפני התאריך. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים).`, 'danger');
      return false;
    }
    
    // בדיקה שלא ניתן לסיים אירוע לפני התאריך שלו
    if (newStatus === 'completed' && now < eventDate) {
      const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      showMessage('שגיאה', `לא ניתן לסיים אירוע לפני התאריך שלו. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לסמן כבוטל או למחוק אותו.`, 'danger');
      return false;
    }
    
    // בדיקה שלא ניתן לשנות סטטוס ל"פעיל/בחוץ" אם האירוע כבר הסתיים או בוטל
    if (newStatus === 'active' && (formData.status === 'completed' || formData.status === 'cancelled')) {
      showMessage('שגיאה', 'לא ניתן לשנות סטטוס ל"פעיל/בחוץ" לאירוע שכבר הסתיים או בוטל.', 'danger');
      return false;
    }
    
    // בדיקה שלא ניתן לשנות סטטוס ל"פעיל/בחוץ" לפני היומיים שלפני התאריך
    if (newStatus === 'active' && now < new Date(eventDate.getTime() - 2 * 24 * 60 * 60 * 1000)) {
      const twoDaysBefore = new Date(eventDate.getTime() - 2 * 24 * 60 * 60 * 1000);
      const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      showMessage('שגיאה', `לא ניתן לשנות סטטוס ל"פעיל/בחוץ" לפני היומיים שלפני התאריך. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן לשנות סטטוס החל מ-${twoDaysBefore.toLocaleDateString('he-IL')}.`, 'danger');
      return false;
    }
    
    return true;
  };

  const canChangeStatus = (newStatus: string) => {
    if (!startDateOnly) return true;
    
    const now = new Date();
    const eventDate = new Date(startDateOnly + ' ' + (startTimeOnly || '00:00'));
    const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // בדיקה שלא ניתן לשנות סטטוס ל"בהכנה" לפני השבוע שלפני התאריך
    if (newStatus === 'preparation' && now < oneWeekBefore) {
      return false;
    }
    
    // בדיקה שלא ניתן לסיים אירוע לפני התאריך שלו
    if (newStatus === 'completed' && now < eventDate) {
      return false;
    }
    
    // בדיקה שלא ניתן לשנות סטטוס ל"פעיל/בחוץ" אם האירוע כבר הסתיים או בוטל
    if (newStatus === 'active' && (formData.status === 'completed' || formData.status === 'cancelled')) {
      return false;
    }
    
    // בדיקה שלא ניתן לשנות סטטוס ל"פעיל/בחוץ" לפני היומיים שלפני התאריך
    if (newStatus === 'active' && now < new Date(eventDate.getTime() - 2 * 24 * 60 * 60 * 1000)) {
      return false;
    }
    
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // בדיקת הגבלות על שינוי סטטוס
    if (name === 'status') {
      if (!checkStatusRestrictions(value)) {
        return; // לא משנים את הסטטוס אם יש הגבלה
      }
      // עדכון הסטטוס אם ההגבלה עברה
      // כלל צבע: אם יוצאים מ'בהכנה' – ננקה צבע
      const nextData = { ...formData, [name]: value } as any;
      if (value !== 'preparation') {
        nextData.color = null;
      }
      setFormData(nextData);
      return;
    }
    
    if (name === 'is_internal') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else if (name.startsWith('description_')) {
        setFormData({ ...formData, [name]: value === '' ? null : value });
    }
    else if (['client', 'location', 'color', 'parent_event'].includes(name)) {
      // אם מנסים לבחור צבע כשלא ב'בהכנה' – נחסום בצד לקוח
      if (name === 'color' && formData.status !== 'preparation') {
        return;
      }
      
      // אם בוחרים מיקום, קבע אוטומטית אם האירוע בפנים או בחוץ
      if (name === 'location') {
        const selectedLocation = locations.find(loc => loc.id === parseInt(value));
        const isIndoor = selectedLocation?.location_type?.includes('פנים') || false;
        setFormData({ ...formData, [name]: value === '' ? null : parseInt(value), is_internal: isIndoor });
      } else {
        setFormData({ ...formData, [name]: value === '' ? null : parseInt(value) });
      }
    } else {
      setFormData({ ...formData, [name]: value === '' ? null : value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitLoading(true);
    
    const finalEndDate = endDateOnly || startDateOnly;
    const finalEndTime = endTimeOnly || '23:59';
    
    const startDateTime = moment(`${startDateOnly} ${startTimeOnly || '00:00'}`).toISOString();
    const endDateTime = moment(`${finalEndDate} ${finalEndTime}`).toISOString();

    const fullDescription = [
        formData.description_amplification || '',
        formData.description_lighting || '',
        formData.description_video || '',
        formData.description_electrical || '',
        formData.description_general || '',
    ].join('---SPEC_DELIMITER---');

    const dataToSend: any = {
      ...formData,
      start_date: startDateTime,
      end_date: endDateTime,
      description: fullDescription,
    };

    delete dataToSend.description_amplification;
    delete dataToSend.description_lighting;
    delete dataToSend.description_video;
    delete dataToSend.description_electrical;
    delete dataToSend.description_general;

    // תיקון 12: ודא שרק אחד מהשדות client/free_text_client נשלח
    if (dataToSend.client && dataToSend.free_text_client) {
        delete dataToSend.free_text_client;
    } else if (!dataToSend.client) {
        delete dataToSend.client;
    }
    // תיקון 12: ודא שרק אחד מהשדות location/free_text_location נשלח
    if (dataToSend.location && dataToSend.free_text_location) {
        delete dataToSend.free_text_location;
    } else if (!dataToSend.location) {
        delete dataToSend.location;
    }
    
    if (!dataToSend.event_name || !dataToSend.start_date || !dataToSend.end_date) {
      setError("שם האירוע, תאריך התחלה ותאריך סיום הם שדות חובה.");
      setSubmitLoading(false);
      return;
    }
    
    try {
      if (eventToEdit && eventToEdit.id) {
        // בדיקה אם התאריכים השתנו
        const originalStartDate = eventToEdit.start_date ? moment(eventToEdit.start_date).format('YYYY-MM-DD') : null;
        const originalEndDate = eventToEdit.end_date ? moment(eventToEdit.end_date).format('YYYY-MM-DD') : null;
        const newStartDate = moment(dataToSend.start_date).format('YYYY-MM-DD');
        const newEndDate = moment(dataToSend.end_date).format('YYYY-MM-DD');
        
        const datesChanged = (originalStartDate !== newStartDate || originalEndDate !== newEndDate);
        
          if (datesChanged) {
          // שימוש בפונקציה החדשה לבדיקת זמינות ציוד
          const response = await axios.post(buildApiUrl(`events/events/${eventToEdit.id}/update-with-equipment-check/`), dataToSend);
          
          // בדיקה אם יש ציוד לא זמין
          if (response.data.requires_confirmation) {
            const unavailableCount = response.data.unavailable_count;
            const unavailableEquipment = response.data.equipment_availability.unavailable_equipment;
            
            // שמירת הנתונים והצגת Modal
            setEquipmentAvailabilityData({
              unavailableCount,
              unavailableEquipment,
              availableCount: response.data.equipment_availability.available_equipment.length
            });
            setPendingFormData(dataToSend);
            setShowEquipmentAvailabilityModal(true);
            return; // לא ממשיכים עם העדכון עד שהמשתמש יבחר
          } else {
            // אין בעיות זמינות - מבצעים עדכון רגיל
            const response = await axios.put<Event>(
              buildApiUrl(`events/events/${eventToEdit.id}/`),
              dataToSend
            );
            onSuccess(response.data);
          }
        } else {
          // התאריכים לא השתנו - עדכון רגיל
          const response = await axios.put<Event>(
            buildApiUrl(`events/events/${eventToEdit.id}/`),
            dataToSend
          );
          onSuccess(response.data);
        }
      } else {
        const response = await axios.post<Event>(
          buildApiUrl('events/events/'),
          dataToSend
        );
        onSuccess(response.data);
      }
    } catch (err) {
      console.error("שגיאה בשליחת טופס אירוע:", err);
      if (axios.isAxiosError(err) && err.response && err.response.data) {
        let errorMessages = '';
        for (const key in err.response.data) {
          if (Object.prototype.hasOwnProperty.call(err.response.data, key)) {
            const errorValue = err.response.data[key];
            if (Array.isArray(errorValue)) {
              errorMessages += `${key}: ${errorValue.join(', ')}\n`;
            } else {
              errorMessages += `${key}: ${errorValue}\n`;
            }
          }
        }
        setError(`שגיאה בשמירת האירוע: \n${errorMessages || 'שגיאה לא ידועה.'}`);
      } else {
        setError("שגיאה בשמירת האירוע. אנא נסה שוב.");
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loadingDeps) return <Spinner animation="border" role="status"><span className="visually-hidden">טוען נתונים...</span></Spinner>;

  return (
    <>
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>פרטי אירוע</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formEventName">
              <Form.Label>שם האירוע</Form.Label>
              <Form.Control type="text" name="event_name" value={formData.event_name} onChange={handleChange} required disabled={isTeamLeader && !!eventToEdit} />
            </Form.Group>
            <Form.Group as={Col} controlId="formClient">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>לקוח (אופציונלי)</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowClientModal(true)}
                  disabled={isTeamLeader && !!eventToEdit}
                >
                  הוסף לקוח חדש
                </Button>
              </div>
              <Form.Select name="client" value={formData.client} onChange={handleChange} disabled={isTeamLeader && !!eventToEdit}>
                <option value="">בחר לקוח...</option>
                {clients.map(client => (<option key={client.id} value={client.id}>{client.client_name}</option>))}
              </Form.Select>
            </Form.Group>
          </Row>
          {/* תיקון 12: שדה טקסט חופשי ללקוח */}
          <Form.Group className="mb-3" controlId="formFreeTextClient">
              <Form.Label>או הזן שם לקוח בטקסט חופשי</Form.Label>
              <Form.Control type="text" name="free_text_client" value={formData.free_text_client || ''} onChange={handleChange} disabled={!!formData.client || (isTeamLeader && !!eventToEdit)} />
          </Form.Group>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="formStartDate">
                <Form.Label>תאריך התחלה</Form.Label>
                <Form.Control type="date" value={startDateOnly} onChange={(e) => setStartDateOnly(e.target.value)} required disabled={isTeamLeader && !!eventToEdit} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formStartTime">
                <Form.Label>שעת התחלה (אופציונלי)</Form.Label>
                <Form.Control type="time" value={startTimeOnly} onChange={(e) => setStartTimeOnly(e.target.value)} disabled={isTeamLeader && !!eventToEdit} />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="formEndDate">
                <Form.Label>תאריך סיום (ברירת מחדל: תאריך התחלה)</Form.Label>
                <Form.Control
                  type="date"
                  value={endDateOnly}
                  min={startDateOnly || undefined}
                  onChange={(e) => setEndDateOnly(e.target.value)}
                  disabled={isTeamLeader && !!eventToEdit}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="formEndTime">
                <Form.Label>שעת סיום (ברירת מחדל: 23:59)</Form.Label>
                <Form.Control
                  type="time"
                  value={endTimeOnly}
                  min={(endDateOnly && startDateOnly && endDateOnly === startDateOnly) ? (startTimeOnly || undefined) : undefined}
                  onChange={(e) => setEndTimeOnly(e.target.value)}
                  disabled={isTeamLeader && !!eventToEdit}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formLocation">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label>מיקום</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowLocationModal(true)}
                  disabled={isTeamLeader && !!eventToEdit}
                >
                  הוסף מיקום חדש
                </Button>
              </div>
              <Form.Select name="location" value={formData.location || ''} onChange={handleChange} disabled={isTeamLeader && !!eventToEdit}>
                <option value="">בחר מיקום...</option>
                {locations.map(location => (<option key={location.id} value={location.id}>{location.location_name} ({location.location_type})</option>))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} controlId="formStatus">
              <Form.Label>סטטוס</Form.Label>
              <Form.Select name="status" value={formData.status} onChange={handleChange} disabled={isTeamLeader && !!eventToEdit}>
                {STATUS_CHOICES.map(status => {
                  const isDisabled = !canChangeStatus(status.value);
                  return (
                    <option 
                      key={status.value} 
                      value={status.value}
                      disabled={isDisabled}
                    >
                      {status.label} {isDisabled ? '(לא זמין)' : ''}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>
          {/* תיקון 12: שדה טקסט חופשי למיקום */}
          <Form.Group className="mb-3" controlId="formFreeTextLocation">
              <Form.Label>או הזן שם מיקום בטקסט חופשי</Form.Label>
              <Form.Control type="text" name="free_text_location" value={formData.free_text_location || ''} onChange={handleChange} disabled={!!formData.location || (isTeamLeader && !!eventToEdit)} />
          </Form.Group>
          {/* תיקון 13: סימון אירוע פנים/חוץ */}
          <Form.Group className="mb-3" controlId="formIsInternal">
            <Form.Check
              type="checkbox"
              label="האם האירוע בתוך מבנה?"
              name="is_internal"
              checked={formData.is_internal}
              onChange={handleChange}
              disabled={isTeamLeader && !!eventToEdit}
            />
          </Form.Group>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formColor">
              <Form.Label>צבע אירוע</Form.Label>
            <Form.Select name="color" value={formData.color || ''} onChange={handleChange} disabled={formData.status !== 'preparation'}>
                <option value="">ללא צבע</option>
                {eventColors.map(color => (
                  <option key={color.id} value={color.id}>
                    {color.name}
                  </option>
                ))}
              </Form.Select>
            {formData.status !== 'preparation' && (
              <small className="text-muted">צבע זמין רק לסטטוס "בהכנה". בעת מעבר סטטוס הצבע יוסר אוטומטית.</small>
            )}
            </Form.Group>
            <Form.Group as={Col} controlId="formParentEvent">
              <Form.Label>אירוע אב</Form.Label>
              <Form.Select name="parent_event" value={formData.parent_event || ''} onChange={handleChange} disabled={isTeamLeader && !!eventToEdit}>
                <option value="">ללא אירוע אב</option>
                {events
                    .filter(e => e.id !== eventToEdit?.id)
                    .map(e => (<option key={e.id} value={e.id}>{e.event_name}</option>))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3" controlId="formDescriptionAmplification">
            <Form.Label>מפרט הגברה</Form.Label>
            <Form.Control as="textarea" rows={2} name="description_amplification" value={formData.description_amplification || ''} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formDescriptionLighting">
            <Form.Label>מפרט תאורה</Form.Label>
            <Form.Control as="textarea" rows={2} name="description_lighting" value={formData.description_lighting || ''} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formDescriptionVideo">
            <Form.Label>מפרט וידאו</Form.Label>
            <Form.Control as="textarea" rows={2} name="description_video" value={formData.description_video || ''} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formDescriptionElectrical">
            <Form.Label>מפרט חשמל</Form.Label>
            <Form.Control as="textarea" rows={2} name="description_electrical" value={formData.description_electrical || ''} onChange={handleChange} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formDescriptionGeneral">
            <Form.Label>הערות כלליות</Form.Label>
            <Form.Control as="textarea" rows={3} name="description_general" value={formData.description_general || ''} onChange={handleChange} />
          </Form.Group>
        </Card.Body>
      </Card>
      
              <div className="d-flex justify-content-end mt-4">
          <Button variant="secondary" onClick={onCancel} className="me-2" disabled={submitLoading}>
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            ביטול
          </Button>
          <Button variant="primary" type="submit" disabled={submitLoading}>
            {submitLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> {eventToEdit ? 'עדכן פרטי אירוע' : 'הוסף אירוע'}</>}
          </Button>
        </div>
    </Form>
    
    {/* מודאל הודעות */}
    <MessageModal
      show={showMessageModal}
      onHide={() => setShowMessageModal(false)}
      title={messageModalTitle}
      message={messageModalContent}
      variant={messageModalVariant}
    />
    
    {/* מודאל בדיקת זמינות ציוד */}
    <Modal show={showEquipmentAvailabilityModal} onHide={handleEquipmentAvailabilityCancel} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
          שינוי תאריכים - בדיקת זמינות ציוד
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          שינוי התאריכים עלול להשפיע על זמינות הציוד!
        </Alert>
        
        {equipmentAvailabilityData && (
          <>
            <div className="mb-3">
              <h6>סיכום זמינות ציוד:</h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="alert alert-success mb-2">
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    <strong>ציוד זמין:</strong> {equipmentAvailabilityData.availableCount} פריטים
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="alert alert-danger mb-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <strong>ציוד לא זמין:</strong> {equipmentAvailabilityData.unavailableCount} פריטים
                  </div>
                </div>
              </div>
            </div>
            
            {equipmentAvailabilityData.unavailableCount > 0 && (
              <div className="mb-3">
                <h6>הציוד הלא זמין לתאריכים החדשים:</h6>
                <div className="list-group">
                  {equipmentAvailabilityData.unavailableEquipment.map((item: any, index: number) => (
                    <div key={index} className="list-group-item list-group-item-danger">
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{item.equipment_name}</h6>
                        <small>
                          {item.quantity_needed} יחידות נדרשות
                          {item.unavailable_quantity && (
                            <span className="text-danger ms-2">
                              ({item.unavailable_quantity} לא זמינות)
                            </span>
                          )}
                        </small>
                      </div>
                      <p className="mb-1 text-muted">
                        <strong>סיבה:</strong> {item.reason}
                        {item.available_quantity !== undefined && (
                          <span className="ms-2">
                            <strong>זמין במלאי:</strong> {item.available_quantity} יחידות
                          </span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <Alert variant="info">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              <strong>הערה:</strong> אם תמשיך עם העדכון, הציוד הלא זמין יישאר באירוע. 
              תוכל להסיר אותו מאוחר יותר או לשנות את התאריכים שוב.
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleEquipmentAvailabilityCancel}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />
          ביטול עדכון
        </Button>
        <Button variant="warning" onClick={handleEquipmentAvailabilityConfirm}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          המשך עם העדכון
        </Button>
      </Modal.Footer>
    </Modal>

    {/* מודאל הוספת לקוח מתוך טופס אירוע */}
    <Modal show={showClientModal} onHide={() => setShowClientModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>הוספת לקוח חדש</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ClientForm
          onSuccess={async () => {
            setShowClientModal(false);
            setReloadFlag(prev => prev + 1);
            await fetchDependencies();
          }}
          onCancel={() => setShowClientModal(false)}
        />
      </Modal.Body>
    </Modal>

    {/* מודאל הוספת מיקום מתוך טופס אירוע */}
    <Modal show={showLocationModal} onHide={() => setShowLocationModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>הוספת מיקום חדש</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LocationForm
          onSuccess={async () => {
            setShowLocationModal(false);
            setReloadFlag(prev => prev + 1);
            await fetchDependencies();
          }}
          onCancel={() => setShowLocationModal(false)}
        />
      </Modal.Body>
    </Modal>
  </>
  );
};

export default EventForm;
